import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { CatalogItem, FavoritesResponse, HistoryResponse, LiveChannel } from '@lumos/contracts';
import {
  clearHistory,
  createChannelSession,
  getFavorites,
  getHistory,
  markWatched,
  removeFromHistory,
  resetProgress,
} from '../api/endpoints.js';
import { Card } from '../components/Card.js';
import { GridSkeleton, RetryError } from '../components/Placeholders.js';
import { SectionWash, useWashItem } from '../components/SectionWash.js';
import { ContinueIcon, FavoritesIcon, LumosMark } from '../components/icons.js';

/** Карточка раздела: сам тайтл и то, сколько от него просмотрено. */
interface Entry {
  item: CatalogItem;
  percent: number;
}

export function LibraryPage({ mode }: { mode: 'favorites' | 'history' }): React.JSX.Element {
  const [entries, setEntries] = useState<Entry[]>([]);
  /**
   * Избранные телеканалы. Отдельной полкой, а не среди карточек: у канала нет
   * ни постера, ни года — в сетке фильмов он был бы пустой плашкой, и открывать
   * его надо не карточкой тайтла, а прямо в плеере.
   */
  const [channels, setChannels] = useState<LiveChannel[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // История — это «что я смотрел», включая досмотренное; «продолжить»
  // отвечает на другой вопрос и досмотренное как раз прячет. По тайтлу
  // оставляем одну, самую свежую карточку: у сериала записей столько же,
  // сколько начатых серий, и полка из одного и того же сериала бесполезна.
  const load = useCallback((): void => {
    setError(null);
    const request = mode === 'favorites' ? getFavorites() : getHistory();
    request
      .then((result: FavoritesResponse | HistoryResponse) => {
        const raw: Entry[] = 'entries' in result
          ? result.items.map(({ item, progress }) => ({ item, percent: progress.percent }))
          : result.items.map((item) => ({ item, percent: 0 }));
        const seen = new Set<string>();
        setEntries(raw.filter((entry) => (seen.has(entry.item.id) ? false : (seen.add(entry.item.id), true))));
        setChannels('channels' in result ? result.channels : []);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Не удалось загрузить раздел'))
      .finally(() => setLoading(false));
  }, [mode]);

  useEffect(load, [load]);

  const title = mode === 'favorites' ? 'Избранное' : 'История просмотра';
  // Кнопка есть только у истории: избранное собирают руками, и стирать его
  // одним нажатием — не то же самое, что убрать список просмотренного.
  const clearable = mode === 'history' && entries.length > 0;

  const listed = useMemo(() => entries.map((entry) => entry.item), [entries]);
  const { washItem, onFocusItem, onLeaveItem } = useWashItem(listed);

  return (
    <main className="app">
      <SectionWash item={washItem} />
      <header className="header">
        <span className="brand">
          <LumosMark className="brand-mark" />
          Lumos
        </span>
        <h1 className="page-title header-title">{title}</h1>
        {clearable && (
          <button
            type="button"
            className="secondary-action header-action"
            onClick={() => {
              void clearHistory().then(() => setEntries([]));
            }}
          >
            Очистить историю
          </button>
        )}
      </header>
      {loading && !error && <GridSkeleton />}
      {error && (
        <RetryError
          message={error}
          onRetry={() => {
            setLoading(true);
            load();
          }}
        />
      )}
      {channels.length > 0 && (
        <section className="catalog-section">
          <h2>Телеканалы</h2>
          <ul className="channel-list">
            {channels.map((channel) => (
              <li key={channel.id} className="channel-item">
                <button
                  type="button"
                  className="channel-button"
                  onClick={() => {
                    void createChannelSession(channel.id)
                      .then((session) => {
                        window.location.hash = `#/watch/${encodeURIComponent(session.sessionId)}`;
                      })
                      .catch(() => setError('Канал не запустился.'));
                  }}
                >
                  <span className="channel-logo-wrap">
                    {channel.logoUrl !== null && (
                      <img className="channel-logo" src={channel.logoUrl} alt="" loading="lazy" />
                    )}
                  </span>
                  <span className="channel-body">
                    <span className="channel-name">{channel.name}</span>
                    {channel.group !== null && <span className="channel-next">{channel.group}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {!loading && !error && entries.length === 0 && channels.length === 0 && (
        <div className="empty-state" role="status">
          <div className="empty-state-icon" aria-hidden="true">
            {mode === 'favorites' ? <FavoritesIcon /> : <ContinueIcon />}
          </div>
          <h2>{mode === 'favorites' ? 'В избранном пока пусто' : 'История пока пуста'}</h2>
          <p>
            {mode === 'favorites'
              ? 'Отложенные фильмы, сериалы и каналы появятся здесь — закладка на странице тайтла.'
              : 'Здесь появится всё, что вы начали смотреть.'}
          </p>
        </div>
      )}
      {entries.length > 0 && (
        <div className="grid">
          {entries.map(({ item, percent }) => (
            <Card
              key={item.id}
              item={item}
              onFocusItem={onFocusItem}
              onLeaveItem={onLeaveItem}
              progressPercent={mode === 'history' ? percent : undefined}
              // Меню по долгому нажатию — только у истории: в избранном эти
              // действия ничего не значат, там другой список и другое намерение.
              actions={
                mode === 'history'
                  ? {
                      onRemove: () => void removeFromHistory(item.id).then(load),
                      onReset: () => void resetProgress(item.id).then(load),
                      onWatched: () => void markWatched(item.id).then(load),
                    }
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </main>
  );
}
